import type { SwitchRootProps } from "@base-ui/react/switch";
import { Switch as BaseSwitch } from "@base-ui/react/switch";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import clsx from "clsx";
import { forwardRef, useMemo } from "react";
import { vars } from "../../theme/theme.css";
import type { LABEL_POSITIONS } from "../../utils/consts";
import { generateColorVariants } from "../../utils/functions";
import { Spinner } from "../Spinner";
import {
  switchLabelClass,
  switchLabelColumnClass,
  switchLabelColumnReverseClass,
  switchLabelRowClass,
  switchLabelRowReverseClass,
  switchRootClass,
  switchSpinnerClass,
  switchThumbClass,
  switchThumbLoadingClass,
  trackColorCheckedHoverVar,
  trackColorCheckedVar,
} from "./styles.css";

interface SwitchProps {
  label?: React.ReactNode;
  labelPosition?: keyof typeof LABEL_POSITIONS;
  customColor?: string;
  isLoading?: boolean;
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  /** Called with the new boolean checked value. Prefer this over onChange. */
  onCheckedChange?: (checked: boolean) => void;
  /** Legacy onChange for backward compatibility — receives a synthetic event with target.checked */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  id?: string;
  name?: string;
  value?: string;
  required?: boolean;
  readOnly?: boolean;
  /** Base UI render prop — renders the switch root as a custom element. */
  render?: SwitchRootProps["render"];
  className?: string;
}

export const Switch = forwardRef(function Switch(
  {
    label,
    labelPosition = "right",
    customColor,
    isLoading,
    checked,
    defaultChecked,
    disabled,
    onCheckedChange,
    onChange,
    id,
    name,
    value,
    required,
    readOnly,
    render,
    className,
  }: SwitchProps,
  ref: React.Ref<HTMLElement>,
) {
  const colorVariants = useMemo(() => {
    return customColor ? generateColorVariants(customColor) : undefined;
  }, [customColor]);

  const handleCheckedChange = (isChecked: boolean) => {
    onCheckedChange?.(isChecked);
    if (onChange) {
      const event = new Event("change", { bubbles: true }) as any;
      Object.defineProperty(event, "target", {
        value: {
          checked: isChecked,
          name: name ?? "",
          value: value ?? "",
          type: "checkbox",
        },
        enumerable: true,
      });
      Object.defineProperty(event, "currentTarget", {
        value: {
          checked: isChecked,
          name: name ?? "",
          value: value ?? "",
          type: "checkbox",
        },
        enumerable: true,
      });
      onChange(event as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const customColorStyles = colorVariants
    ? assignInlineVars({
        [trackColorCheckedVar]: colorVariants.default,
        [trackColorCheckedHoverVar]: colorVariants.dark,
      })
    : undefined;

  const flexDirectionClassMap: Record<keyof typeof LABEL_POSITIONS, string> = {
    left: switchLabelRowClass,
    right: switchLabelRowReverseClass,
    top: switchLabelColumnClass,
    bottom: switchLabelColumnReverseClass,
  };
  const flexDirectionClass = flexDirectionClassMap[labelPosition];

  return (
    <label
      className={clsx(switchLabelClass, flexDirectionClass, className)}
      aria-disabled={disabled || isLoading}
      data-slot="switch-root"
    >
      {label && <span data-slot="switch-label">{label}</span>}
      <BaseSwitch.Root
        ref={ref}
        id={id}
        name={name}
        checked={checked}
        defaultChecked={defaultChecked}
        disabled={disabled || isLoading}
        required={required}
        readOnly={readOnly}
        value={value}
        onCheckedChange={handleCheckedChange}
        className={switchRootClass}
        style={customColorStyles}
        data-slot="switch-track"
        {...(render !== undefined && { render })}
      >
        <BaseSwitch.Thumb
          className={
            isLoading
              ? `${switchThumbClass} ${switchThumbLoadingClass}`
              : switchThumbClass
          }
          data-slot="switch-thumb"
        />
      </BaseSwitch.Root>
      {isLoading && (
        <Spinner
          size={10}
          color={vars.colors.neutral.lighter}
          className={switchSpinnerClass}
        />
      )}
    </label>
  );
});

Switch.displayName = "Switch";
